import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ReceiptRefundIcon
} from '@heroicons/react/20/solid';

const GiftCardDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null); // objeto con giftCard y transactions
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://narella.nistal.net/api/giftcards/data/${id}`);
        if (!res.ok) {
          throw new Error("Error al obtener los detalles de la tarjeta regalo");
        }
        const result = await res.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-pink-50 to-white">
        <p className="text-2xl text-pink-600 animate-pulse">Cargando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-pink-50 to-white">
        <p className="text-2xl text-red-600">{error}</p>
      </div>
    );
  }

  if (!data || !data.giftCard) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-pink-50 to-white">
        <p className="text-2xl text-gray-600">Tarjeta regalo no encontrada.</p>
      </div>
    );
  }

  const { giftCard, transactions } = data;
  const buyerName = giftCard.client_nombre
    ? `${giftCard.client_nombre.toUpperCase()} ${giftCard.client_apellido.toUpperCase()}`
    : 'N/A';

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth()+1)
      .toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const totalServicios = giftCard.servicios
    ? giftCard.servicios.reduce((sum, s) => sum + Number(s.valor), 0)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl font-extrabold text-center text-pink-800 mb-8 flex items-center justify-center gap-2 drop-shadow-sm">
          <CreditCardIcon className="h-10 w-10 text-pink-600" />
          Tarjeta Regalo
        </h1>

        {/* Card Info Box */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-pink-100">
          <div className="p-6 space-y-8">
            {/* Balance */}
            <div className="bg-pink-50 rounded-xl p-5 text-center shadow-inner border border-pink-100">
              <p className="text-lg text-pink-700 font-semibold">Saldo Actual</p>
              <p className="text-4xl font-bold text-green-600 mt-1">${giftCard.current_balance}</p>
            </div>

            {/* Detalles */}
            <section>
              <h2 className="text-2xl font-bold text-pink-700 text-center mb-6 flex items-center justify-center gap-2">
                <ClipboardDocumentListIcon className="h-7 w-7 text-pink-500" />
                Detalles
              </h2>
              <div className="space-y-4 text-pink-900 text-lg">
                {/* Código */}
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2">
                    <CreditCardIcon className="h-6 w-6 text-pink-400" />
                    <span className="font-medium">Código</span>
                  </div>
                  <span className="font-bold">{giftCard.public_code}</span>
                </div>
                {/* Monto */}
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2">
                    <CurrencyDollarIcon className="h-6 w-6 text-green-500" />
                    <span className="font-medium">Monto</span>
                  </div>
                  <span className="font-bold text-green-600">${giftCard.initial_value}</span>
                </div>
                {/* Estado */}
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2">
                    {giftCard.state === 'active' ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    ) : (
                      <ExclamationCircleIcon className="h-6 w-6 text-yellow-500" />
                    )}
                    <span className="font-medium">Estado</span>
                  </div>
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-pink-100 text-pink-800">
                    {giftCard.state}
                  </span>
                </div>
                {/* Emitida */}
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-6 w-6 text-pink-400" />
                    <span className="font-medium">Emitida</span>
                  </div>
                  <span className="font-bold">{formatDate(giftCard.issued_at)}</span>
                </div>
                {/* Válida hasta */}
                {giftCard.expires_at && (
                  <div className="flex items-center justify-between border-b pb-2">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-6 w-6 text-pink-400" />
                      <span className="font-medium">Válida hasta</span>
                    </div>
                    <span className="font-bold">{formatDate(giftCard.expires_at)}</span>
                  </div>
                )}
                {/* Comprada por */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-6 w-6 text-pink-400" />
                    <span className="font-medium">De</span>
                  </div>
                  <span className="font-bold">{buyerName}</span>
                </div>
                {/* Destinatario */}
                <div className="flex items-center justify-between border-t pt-2">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-6 w-6 text-pink-400" />
                    <span className="font-medium">Para</span>
                  </div>
                  <span className="font-bold">{giftCard.destinatario_nombre}</span>
                </div>
                {/* Servicios */}
                {giftCard.servicios && giftCard.servicios.length > 0 && (
                  <div className="border-t pt-2">
                    <h3 className="text-xl font-bold text-pink-700 mb-2">Servicios</h3>
                    <ul className="space-y-2">
                      {giftCard.servicios.map((servicio) => (
                        <li key={servicio.servicio_id} className="flex justify-between">
                          <span>{servicio.nombre}</span>
                          <span className="font-bold">${servicio.valor}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardDetail;
